import {api} from "@/shared/api";
import {BaseTree, BaseTreeBasicNode} from "@/shared/ui/baseTree";
import {FancytreeContextMenu} from "@/shared/ui/fancytreeContextMenu";
import {FormUtil} from "@/shared/forms/formUtil";
import {promiseNow} from "@/shared/utils/promise";
import {UserAssignmentPanel} from "@/shared/admin/basicSameTypeAssignmentPanel";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import * as Button from "@/shared/utils/button";
import {debounce, toggleNavigationTabs} from "@/shared/utils/misc";
import {Notify} from "@/shared/ui/notify";
import {SecurityStatus} from "@/shared/securityStatus";
import {AssignedUser, ErrorResponse, Group, Identifiable} from "@/shared/timr";
import FancytreeNode = Fancytree.FancytreeNode;
import FancytreeDndData = Fancytree.FancytreeDndData;
import {toAssignableUser} from "@/shared/admin/toAssignableUserHelper";


export interface ClipboardGroup extends Identifiable {
	name: string;
}

type ReloadDataForEditedNodesParams = {
	draggedGroup: Group;
	targetGroup: Group;
	oldParentGroup: Group;
	newParentGroup: Group;
};

enum GroupOnGroupActionType {
	MOVE = "MOVE",
	MERGE = "MERGE"
}

export class GroupsAdministrationPage {

	private tree: BaseTree;

	private dialogHandler: GroupDialogHandler;

	private clipboardGroup: ClipboardGroup = {id: null, name: ""};
	private groupPasteDialog: BootstrapModal.Modal;
	private groupOnGroupDialog: BootstrapModal.Modal;
	private groupPasteId: number;
	private dragDropData: FancytreeDndData;

	constructor() {
		this.tree = new BaseTree({
			treeElement: $("#groupTree"),
			loadRootNodesURL: "admin/loadAllGroups.json",
			loadSubNodesURL: "admin/loadSubGroups.json",
			filterNodesURL: "admin/filterGroups.json",
			// DEVNOTE: ({}) instead of {} to return a new object every time instead of void
			getRequestData: () => ({}),
			isNodeSelectable: () => false,
			// The config stores context menus as FancytreeContextMenu<unknown>; the type parameter is only used in contravariant (action) positions, so the Group-typed menu is safe here.
			contextMenu: this.createContextMenu() as FancytreeContextMenu<unknown>,
			clickFolderMode: 3,
			dndSettings: this.getDndSettings(),
		});

		this.dialogHandler = new GroupDialogHandler();
		$(this.dialogHandler)
			.on(GroupDialogHandler.EVENT_GROUP_UPDATED, (event, group: Group) => {
				const parentGroup = group.parentGroup;

				if (parentGroup != null) {
					const parent = this.tree.getNodeByKey("" + parentGroup.id);

					if (parent != null) {
						if (parent.isLazy()) {
							parent.load(true);
							parent.setExpanded(true);

							// if a group (which has a parent) is deleted, the parent group must be updated for a correct state and version (TIMR-6403)
							parent.data = parentGroup;
						} else {
							this.tree.queryTree("");
						}
					}
				} else {
					this.tree.queryTree("");
				}

				if (group.id === this.clipboardGroup?.id) {
					this.clipboardGroup.name = group.name;
					this.updateCopyDisplay();
				}
			})
			.on(GroupDialogHandler.EVENT_GROUP_DELETE, (event, group: Group) => {
				this.deleteGroup(group);
			});

		$("#groupAddButton").click(() => {
			this.dialogHandler.resetDialog();
			this.dialogHandler.show();
		});

		const filterGroupTree = () => {
			this.tree.queryTree($("#groupFilter").val() as string);
		};

		$("#groupFilter").on("input", debounce(filterGroupTree, this, 350));

		this.initListeners();
	}

	private createContextMenu() {
		return new FancytreeContextMenu<Group>(true)
			.addMenuEntry({
				label: "groups.context.edit",
				action: this.editGroup
			})
			.addMenuEntry({
				label: "groups.context.delete",
				action: this.deleteGroup
			})
			.addMenuEntry({
				label: "groups.context.pasteGroup",
				action: group => this.pasteGroup(group),
				active: group => this.clipboardGroup?.id != null
			})
			.addMenuEntry({
				label: "groups.context.copyGroup",
				action: group => this.copyGroup(group)
			})
			.addDivider()
			.addMenuEntry({
				label: "groups.context.addsubgroup",
				show: () => SecurityStatus.isCorporateFeaturesAllowed(),
				action: this.addSubGroup
			});
	}

	private editGroup = (group: Group) => {
		this.dialogHandler.setGroup(group);
		this.dialogHandler.show();
	}

	private deleteGroup = (group: Group) => {
		BootstrapModal.confirm({
			title: "dialog.warning.title",
			message: messages.withArguments("dialog.delete.group.message", group.name),
			yesLabel: "confirmation.delete",
			yesAction: modal => {
				api("admin/deleteGroup.json")
					.post({
						id: group.id
					})
					.then(data => {
						SecurityStatus.update(data.securityStatus);

						// remove node of fancytree list without reload
						this.tree.getNodeByKey("" + group.id).remove();

						// if a group (which has a parent) is deleted, the version of the parent group must be updated (TIMR-6403)
						if (data.parentGroup != null) {
							const parent = this.tree.getNodeByKey("" + group.parentGroup.id);
							// if a group (which has a parent) is deleted, the parent group must be updated for a correct state and version (TIMR-6403)
							parent.data = data.parentGroup;
						}
						this.dialogHandler.close();
						modal.close();
					});
			}
		});
	}

	private addSubGroup = (group: Group) => {
		this.dialogHandler.setGroup({
			parentGroup: group
		});
		this.dialogHandler.show();
	}

	private initListeners() {
		$("#pasteAsRootButton").click(() => {
			this.pasteGroup(null);
		});
		$("#clearGroupClipboardButton").click(() => {
			this.clearClipboard();
		});
	}

	protected getDndSettings() {
		return {
			autoExpandMS: 400,
			focusOnClick: false,
			preventVoidMoves: true,
			preventRecursiveMoves: true,
			dragStart: (node: FancytreeNode, data: FancytreeDndData) => {
				this.tree.config.treeElement.find("ul").addClass("fancytree-hide-menu");
				const nodeData = node.data as BaseTreeBasicNode;
				// Make sure dragged node is not a dummy or loading/error node
				return !(nodeData?.dummy === true || node.statusNodeType != null);
			},
			dragEnter: (node: FancytreeNode, data: FancytreeDndData) => {
				const draggedNode = data.otherNode;
				const draggedNodeParent = draggedNode.getParent();
				const nodeParent = node.getParent();

				const dragModes: Fancytree.Extensions.HitMode[] = [];

				// Don't allow drag to its own parent
				if (!draggedNode.isChildOf(node)) {
					dragModes.push("over");
				}

				// Only allow drag "before" to root level and if node is not already at root level
				if (nodeParent?.isRootNode() && !draggedNodeParent?.isRootNode()) {
					dragModes.push("before");
				}

				return dragModes;
			},
			dragDrop: (node: FancytreeNode, data: FancytreeDndData) => {
				if (data.hitMode === "over") {
					if (node.isLazy()) {
						node.load();
					}

					this.dragDropData = data;
					this.showDropGroupDialog();
				} else if (data.hitMode === "before") {
					this.dragDropData = {
						otherNode: data.otherNode
					};
					this.dropGroupSave();
				}
			},
			dragStop: (node: FancytreeNode, data: FancytreeDndData) => {
				this.tree.config.treeElement.find("ul").removeClass("fancytree-hide-menu");
			}
		};
	}

	protected showDropGroupDialog() {
		if (!this.groupOnGroupDialog) {
			this.groupOnGroupDialog = new BootstrapModal.Modal({
				title: "groupongroup.dialog.text",
				titleSize: "h5",
				message: $("#groupOnGroupDialog"),
				buttons: [{
					text: "dialog.save",
					action: () => {
						this.dropGroupSave();
						this.groupOnGroupDialog.close();
					},
					css: "btn-primary"
				}, {
					text: "dialog.cancel",
					action: () => {
						this.groupOnGroupDialog.close();
					}
				}],
				backdrop: true
			});
		}
		this.groupOnGroupDialog.show();
	}

	protected dropGroupSave() {
		const draggedNode = this.dragDropData.otherNode;
		const targetNode = this.dragDropData.node;

		const draggedGroup = draggedNode?.data as Group;
		const targetGroup = targetNode?.data as Group;

		// If targetGroup is not defined move group to root, otherwise the user can choose between move or merge
		const groupAction = targetGroup?.id
			? $(`input[name="groupAction"]:checked`).val()
			: GroupOnGroupActionType.MOVE;

		api("admin/handleGroupOnGroup.json")
			.post({
				draggedGroupId: draggedGroup?.id,
				targetGroupId: targetGroup?.id,
				groupAction: groupAction
			})
			.done((data: ReloadDataForEditedNodesParams) => {
				this.reloadDataForEditedNodes(data);
				if (targetNode) {
					if (groupAction === GroupOnGroupActionType.MERGE) {
						draggedNode.remove();
						this.reloadAndExpandNode(targetNode.key);
					} else if (groupAction === GroupOnGroupActionType.MOVE) {
						draggedNode.moveTo(targetNode);
						// DEVNOTE: The following line is needed to remove the
						// dragged node from the old parent node in the dom.
						// Otherwise, a ghost node will be displayed.
						$(draggedNode.li).remove();
						targetNode.setExpanded(true);
					}
				} else {
					draggedNode.moveTo(this.tree.getRoot());
				}

				// resort the tree to show the correct order after moving/merging
				this.sortTree();
			});
	}

	private copyGroup(group: Group) {
		this.clipboardGroup = group;
		Notify.success(messages["group.clipboard.copy"]);
		this.updateCopyDisplay();
	}

	private clearClipboard() {
		this.clipboardGroup = null;
		this.updateCopyDisplay();
	}

	/**
	 * Opens paste group dialog if a group is in the clipboard
	 * otherwise show an error dialog
	 * @param group If null, clipboard group gets pasted as top-level element
	 */
	private pasteGroup(group: Group) {
		this.groupPasteId = group?.id;
		FormUtil.removeAllFeedback($("#groupPasteForm"));
		if (this.clipboardGroup?.id) {
			const groupPasteNewName = $("#groupPasteNewName");
			this.getPasteGroupDialog().show();
			groupPasteNewName.val(`${this.clipboardGroup.name} (${messages["tooltip.copy"]})`);
		} else {
			BootstrapModal.error({
				title: "dialog.error.title",
				message: "group.pasteNoGroup"
			});
		}
	}

	private savePasteGroupDialog() {
		api("admin/pasteGroup.json")
			.post({
				targetId: this.groupPasteId,	// If null, paste as a top-level element
				clipboardGroupId: this.clipboardGroup.id,
				name: $("#groupPasteNewName").val()
			})
			.always(() => {
				this.groupPasteDialog.resetLoadingButtons();
			})
			.done(data => {
				this.clearClipboard();
				$("#groupPasteNewName").val("");
				this.closePasteGroupDialog();
				$(this.dialogHandler).triggerHandler(GroupDialogHandler.EVENT_GROUP_UPDATED, data.group);
				this.reloadAndExpandNode(this.groupPasteId);
				this.sortTree();
				Notify.success(messages["group.clipboard.success"]);
			})
			.fail((response: ErrorResponse) => {
				if (response.responseJSON?.fieldErrors) {
					FormUtil.markFieldErrors($("#groupPasteForm"), response.responseJSON.fieldErrors);
				}
			});
	}

	private closePasteGroupDialog() {
		this.groupPasteDialog.close();
	}

	private getPasteGroupDialog() {
		if (this.groupPasteDialog == null) {
			this.groupPasteDialog = this.createPasteGroupDialog();
		}
		return this.groupPasteDialog;
	}

	private createPasteGroupDialog() {
		return new BootstrapModal.Modal({
			title: "pasteGroupName.header",
			message: $(`#groupPasteForm`),
			buttons: [{
				text: "dialog.save",
				action: () => {
					this.savePasteGroupDialog();
				},
				css: "btn-primary"
			}, {
				text: "dialog.cancel",
				action: () => {
					this.closePasteGroupDialog();
				}
			}]
		});
	}

	private reloadAndExpandNode(id: number | string) {
		if (id != null) {
			const parent = this.tree.getNodeByKey(id + "");

			if (parent) {
				parent.lazy = true;
				parent.load(true).done(() => {
					parent.setExpanded(true);
				});
			}
		} else {
			this.tree.reload();
		}
	}

	private updateCopyDisplay() {
		const panel = $("#copiedGroupInfoPanel");
		const element = $("#copiedGroupInfo");

		if (this.clipboardGroup) {
			panel.show();
			element.text(this.clipboardGroup.name);
		} else {
			panel.hide();
			element.text("");
		}
	}

	private reloadDataForEditedNodes(data: ReloadDataForEditedNodesParams) {
		// Dragged node
		if (data?.draggedGroup) {
			const draggedNode = this.tree.getNodeByKey(data?.draggedGroup?.key);
			if (draggedNode) {
				draggedNode.data = data?.draggedGroup;
			}
		}
		// Target node
		if (data?.targetGroup) {
			const targetNode = this.tree.getNodeByKey(data?.targetGroup?.key);
			if (targetNode) {
				targetNode.data = data?.targetGroup;
			}
		}
		// Old parent node
		if (data?.oldParentGroup) {
			const oldParentNode = this.tree.getNodeByKey(data?.oldParentGroup?.key);
			if (oldParentNode) {
				oldParentNode.data = data?.oldParentGroup;
			}
		}
		// New parent node
		if (data?.newParentGroup) {
			const newParentNode = this.tree.getNodeByKey(data?.newParentGroup?.key);
			if (newParentNode) {
				newParentNode.data = data?.newParentGroup;
			}
		}
	}

	private sortTree() {
		this.tree.getRoot().sortChildren((a, b) => a.title.localeCompare(b.title), true);
	}
}

class GroupDialogHandler {

	public static EVENT_GROUP_UPDATED = "eventGroupUpdated";
	public static EVENT_GROUP_DELETE = "eventGroupDelete";

	private modal: BootstrapModal.Modal;
	private leaderAssignmentPanel: UserAssignmentPanel;
	private memberAssignmentPanel: UserAssignmentPanel;

	private group: Partial<Group>;

	constructor() {
		this.modal = new BootstrapModal.Modal({
			title: "group.header.create",
			message: $("#groupDialog"),
			destroyOnClose: false,
			size: "modal-xl",
			buttons: [{
				text: "dialog.save",
				css: "saveButton btn-primary",
				action: this.sendGroupSaveRequest
			}, {
				text: "dialog.cancel",
				css: "cancelButton btn-default",
			}, {
				text: "dialog.delete",
				css: "deleteButton btn-outline-danger",
				left: true,
				action: () => { $(this).triggerHandler(GroupDialogHandler.EVENT_GROUP_DELETE, this.group); }
			}],
			onOpen: () => {
				this.resizeAssignmentPanels();

				// Initial redraw so table header is correctly spaced
				this.leaderAssignmentPanel.updateView();

				$("#groupName").focus();
			},
			onClose: () => {
				this.leaderAssignmentPanel.setEditMode(false);
				this.memberAssignmentPanel.setEditMode(false);
				FormUtil.switchTabNavigationToFirst($("ul.dialog-nav-tabs"));
			}
		});

		// Resize assignment panels on window size change
		$(window).resize(debounce(() => this.resizeAssignmentPanels(), this, 50));

		// Users stay the same so only load promise once
		const userSource = api("admin/getAssignableUsers.json").get()
			.then(response => response.data.map(toAssignableUser));

		this.leaderAssignmentPanel = new UserAssignmentPanel({
			panelElement: $("#groupLeaderAssignmentPanel"),
			assignedSourceProvider: (): JQueryPromise<AssignedUser[]> => {
				const groupId = $("#groupId").val();

				// For a new subgroup we load the parents leaders as autoAssigned users
				const loadGroupId = Number(groupId || $("#groupParentGroupId").val());

				if (loadGroupId) {
					return api("admin/loadGroupLeaders.json")
						.get({
							groupId: loadGroupId
						})
						.then(leaders => this.setLeadersAutoAssigned(leaders, !groupId));
				} else {
					return promiseNow<AssignedUser[]>([]);
				}
			},
			selectSourceProvider: () => userSource,
			editMessage: "groups.assign.leader",
			saveMessage: "admin.assignment.edit.end"
		});

		this.memberAssignmentPanel = new UserAssignmentPanel({
			panelElement: $("#groupMemberAssignmentPanel"),
			assignedSourceProvider: (): JQueryPromise<AssignedUser[]> => {
				const groupId = $("#groupId").val();

				if (groupId) {
					return api("admin/loadGroupMembers.json")
						.get({
							groupId: groupId
						})
						// endpoint returns plain users; members are never auto-assigned, so the panel reads autoAssigned as falsy either way
						.then(members => members as AssignedUser[]);
				} else {
					return promiseNow<AssignedUser[]>([]);
				}
			},
			selectSourceProvider: () => userSource,
			editMessage: "groups.assign.member",
			saveMessage: "admin.assignment.edit.end"
		});

		this.leaderAssignmentPanel.onUnassignment(user => {
			if (this.memberAssignmentPanel.getAssigned().some(u => u.id === user.id)) {
				BootstrapModal.confirm({
					title: "dialog.question.title",
					message: messages.withArguments("dialog.unassign.groupmember.message", user.fullname),
					yesAction: modal => {
						this.memberAssignmentPanel.unassign(user);
						modal.close();
					},
					yesLabel: "confirmation.remove",
					noLabel: "confirmation.keep"
				});
			}
		});

		const onEditModeChanged = () => {
			const editMode = this.leaderAssignmentPanel.isInEditMode() || this.memberAssignmentPanel.isInEditMode();
			this.modal.toggleButton("saveButton", !editMode);
			this.modal.toggleButton("cancelButton", !editMode);
			this.modal.toggleButton("deleteButton", !editMode && this.group.id !== null);

			toggleNavigationTabs("#tabNavigation", editMode);
		};

		this.leaderAssignmentPanel.onEditModeChanged(onEditModeChanged);
		this.memberAssignmentPanel.onEditModeChanged(onEditModeChanged);

		$('a[data-toggle="tab"]').on("shown.bs.tab", e => {
			const tabId = $(e.target).attr("href");
			if (tabId === "#tabGroupLeaders") {
				this.leaderAssignmentPanel.updateView();
			} else if (tabId === "#tabGroupMembers") {
				this.memberAssignmentPanel.updateView();
			}
		});

		$("#groupName").change(() => this.updateTitle());
	}

	public show() {
		this.modal.show();
	}

	public close() {
		this.modal.close();
	}

	public setGroup(group: Partial<Group>) {
		this.group = group;

		this.modal.toggleButton("deleteButton", this.group.id != null);

		$("#groupId").val(group.id);
		$("#groupVersion").val(group.version);
		$("#groupName").val(group.name);
		$("#groupDescription").val(group.description);
		$("#groupExternalId").val(group.externalId);
		$("#groupRequiresSubstituteOverride").val(group.requiresSubstituteOverride == null ? "" : String(group.requiresSubstituteOverride));
		$("#groupParentGroupId").val(group.parentGroup != null ? group.parentGroup.id : null);

		// reset filter
		$("#memberNameFilter").val("");
		$("#leaderNameFilter").val("");

		// reset assignment panel
		this.leaderAssignmentPanel.reloadAssigned();
		this.memberAssignmentPanel.reloadAssigned();

		this.updateTitle();
		FormUtil.removeAllFeedback($("#groupForm"), true);
	}

	private updateTitle() {
		let message = messages[this.group.id == null ? "group.header.create" : "group.header.edit"];

		const groupName = $("#groupName").val() as string;
		if (groupName) {
			message += ": " + groupName;
		}

		this.modal.setTitle(message, true);
	}

	public resetDialog() {
		this.setGroup({
			id: null,
			version: null,
			name: "",
			description: "",
			requiresSubstituteOverride: null,
			externalId: "",
		});
	}

	/**
	 * Decides which leaders are marked as autoAssigned to style the elements and for a correct save request, there are two options:
	 * if subgroup is added (!groupId)		-> all leaders should be auto assigned (duplicated elements are removed)
	 * if group gets edited (addSubtask)	-> backend delivers autoAssigned information
	 * user IDs must be unique for the assignment and saving logic, therefore auto assigned leaders IDs are set blank to allow both manually and auto assigned leaders
	 * @param leaders
	 * @param addSubtask
	 * @private
	 */
	private setLeadersAutoAssigned(leaders: AssignedUser[], addSubtask: boolean): AssignedUser[] {
		return leaders
			.filter((user, i) => !addSubtask || leaders.findIndex(l => l.id === user.id) === i) // duplicated elements are removed if subtask is added
			.map(user => {
				if (user.autoAssigned || addSubtask) {
					user.autoAssigned = true;
					// @ts-expect-error string value used instead of number
					user.id = ""; // set id blank to allow both manually assigned leaders
				}
				return user;
			});
	}

	private sendGroupSaveRequest = (modal: BootstrapModal.Modal, button: JQuery) => {
		const groupForm = $("#groupForm");
		FormUtil.removeAllFeedback(groupForm, true);
		Button.setLoading(button);

		const leaders = this.leaderAssignmentPanel.getAssigned()
			.filter(user => !user.autoAssigned) // remove auto assigned users
			.map(user => ({
				name: "leaders",
				value: "" + user.id
			}));

		const members = this.memberAssignmentPanel.getAssigned().map(user => ({
			name: "members",
			value: "" + user.id
		}));

		const serializedGroup = groupForm.serializeArray();
		serializedGroup.push(...leaders);
		serializedGroup.push(...members);

		api("admin/saveGroup.json")
			.post(serializedGroup)
			.done(data => {
				$(this).triggerHandler(GroupDialogHandler.EVENT_GROUP_UPDATED, data.group);

				this.modal.close();
			})
			.fail((response: ErrorResponse) => {
				if (response.responseJSON?.fieldErrors) {
					FormUtil.markFieldErrors(groupForm, response.responseJSON.fieldErrors, true);
				}
			})
			.always(() => {
				Button.resetLoading(button);
			});
	}

	private resizeAssignmentPanels() {
		const screenHeight = $(window).height();

		// Estimated height of other dialog components + padding + margin ...
		const panelHeight = Math.max(screenHeight - 650, 300);
		this.leaderAssignmentPanel.setScrollHeight(panelHeight);
		this.memberAssignmentPanel.setScrollHeight(panelHeight);
	}
}
